import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import WalletBalance from './components/WalletBalance';
import TransactionHistory from './components/TransactionHistory';
import CampusAmbassadorProgram from './components/CampusAmbassadorProgram';
import PickupPointsManager from './components/PickupPointsManager';
import BudgetTracker from './components/BudgetTracker';
import StudentIdIntegration from './components/StudentIdIntegration';

const UniversityWallet = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [walletData, setWalletData] = useState({
    balance: 125.50,
    monthlySubsidy: 200.00,
    remainingCredits: 8,
    renewalDate: new Date('2025-12-01')
  });

  const [transactions] = useState([
    {
      id: 1,
      type: 'purchase',
      description: 'Bolsa Sorpresa - Café Central',
      amount: -15.50,
      date: new Date('2025-11-07T14:30:00'),
      business: 'Café Central',
      savings: 18.50,
      impact: { co2: 2.3, water: 45 }
    },
    {
      id: 2,
      type: 'subsidy',
      description: 'Subsidio Mensual Noviembre',
      amount: 200.00,
      date: new Date('2025-11-01T09:00:00')
    },
    {
      id: 3,
      type: 'purchase',
      description: 'Bolsa Sorpresa - Panadería Luna',
      amount: -12.00,
      date: new Date('2025-11-06T16:45:00'),
      business: 'Panadería Luna',
      savings: 15.00,
      impact: { co2: 1.8, water: 32 }
    },
    {
      id: 4,
      type: 'bonus',
      description: 'Bonificación por Referencia',
      amount: 25.00,
      date: new Date('2025-11-05T11:20:00')
    },
    {
      id: 5,
      type: 'purchase',
      description: 'Bolsa Sorpresa - Restaurante Verde',
      amount: -22.00,
      date: new Date('2025-11-04T19:15:00'),
      business: 'Restaurante Verde',
      savings: 28.00,
      impact: { co2: 3.1, water: 58 }
    }
  ]);

  const [ambassadorData] = useState({
    level: 'silver',
    nextLevel: 'gold',
    totalReferrals: 12,
    monthlyReferrals: 3,
    creditsEarned: 150.00,
    monthlyCredits: 45.00,
    campusRank: 8,
    totalAmbassadors: 156,
    currentLevelProgress: 12,
    nextLevelTarget: 20,
    referralCode: 'UCM-SG2025'
  });

  const [referrals] = useState([
    {
      id: 1,
      name: 'Carlos Mendoza',
      joinDate: new Date('2025-10-15'),
      creditEarned: 15.00,
      status: 'active'
    },
    {
      id: 2,
      name: 'Ana Rodríguez',
      joinDate: new Date('2025-10-28'),
      creditEarned: 15.00,
      status: 'active'
    },
    {
      id: 3,
      name: 'Miguel Torres',
      joinDate: new Date('2025-11-02'),
      creditEarned: 15.00,
      status: 'pending'
    }
  ]);

  const [challenges] = useState([
    {
      id: 1,
      title: 'Rescatador del Mes',
      description: 'Rescata 15 bolsas sorpresa este mes',
      progress: 8,
      target: 15,
      unit: 'bolsas',
      reward: 50.00,
      timeLeft: 24,
      icon: 'Target'
    },
    {
      id: 2,
      title: 'Embajador Social',
      description: 'Refiere a 5 nuevos estudiantes',
      progress: 3,
      target: 5,
      unit: 'referencias',
      reward: 75.00,
      timeLeft: 18,
      icon: 'Users'
    }
  ]);

  const [pickupPoints] = useState([
    {
      id: 1,
      name: 'Cafetería Central',
      location: 'Edificio Principal, Planta Baja',
      icon: 'Coffee',
      distance: 0.2,
      hours: { open: '07:00', close: '20:00' },
      availability: 'high',
      currentCapacity: 5,
      maxCapacity: 20,
      services: ['WiFi gratuito', 'Área de espera', 'Refrigeración'],
      accessibility: true
    },
    {
      id: 2,
      name: 'Centro Estudiantil',
      location: 'Edificio de Servicios Estudiantiles',
      icon: 'Users',
      distance: 0.4,
      hours: { open: '08:00', close: '22:00' },
      availability: 'medium',
      currentCapacity: 12,
      maxCapacity: 15,
      services: ['Casilleros disponibles', 'Área de descanso', 'Carga de dispositivos'],
      accessibility: true
    },
    {
      id: 3,
      name: 'Biblioteca Principal',
      location: 'Edificio de Biblioteca, Entrada Principal',
      icon: 'BookOpen',
      distance: 0.6,
      hours: { open: '06:00', close: '24:00' },
      availability: 'high',
      currentCapacity: 3,
      maxCapacity: 10,
      services: ['Silencioso', 'Estudio 24/7', 'Seguridad'],
      accessibility: true
    }
  ]);

  const [favoritePoints] = useState([
    {
      id: 1,
      name: 'Cafetería Central',
      icon: 'Coffee',
      distance: 0.2,
      availability: 'high'
    },
    {
      id: 3,
      name: 'Biblioteca Principal',
      icon: 'BookOpen',
      distance: 0.6,
      availability: 'high'
    }
  ]);

  const [budgetData] = useState({
    week: {
      budget: 50.00,
      spent: 27.50,
      remaining: 22.50,
      categories: [
        { id: 'food', name: 'Comidas', amount: 15.50, transactions: 1 },
        { id: 'beverages', name: 'Bebidas', amount: 12.00, transactions: 2 }
      ]
    },
    month: {
      budget: 200.00,
      spent: 74.50,
      remaining: 125.50,
      categories: [
        { id: 'food', name: 'Comidas', amount: 37.50, transactions: 3 },
        { id: 'beverages', name: 'Bebidas', amount: 22.00, transactions: 4 },
        { id: 'snacks', name: 'Snacks', amount: 15.00, transactions: 2 }
      ]
    },
    semester: {
      budget: 800.00,
      spent: 324.50,
      remaining: 475.50,
      categories: [
        { id: 'food', name: 'Comidas', amount: 180.50, transactions: 15 },
        { id: 'beverages', name: 'Bebidas', amount: 89.00, transactions: 18 },
        { id: 'snacks', name: 'Snacks', amount: 55.00, transactions: 8 }
      ]
    }
  });

  const [spendingInsights] = useState({
    dailyAverage: 3.72,
    dailyTrend: 12.5,
    favoriteTime: '14:00-16:00',
    favoriteTimePercentage: 35
  });

  const [recommendations] = useState([
    { text: 'Considera aumentar tu presupuesto semanal en $10 MXN para aprovechar más ofertas' },
    { text: 'Las bolsas de la tarde (14:00-16:00) suelen tener mejor relación calidad-precio' },
    { text: 'Podrías ahorrar $25 MXN más este mes eligiendo bolsas de categoría "Comidas"' }
  ]);

  const [studentData] = useState({
    name: 'Sofía García Martínez',
    studentId: '202145678901',
    career: 'Ingeniería en Sistemas',
    faculty: 'Facultad de Informática',
    semester: 6,
    enrollmentYear: 2021,
    status: 'Activo',
    photo: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face',
    expiryDate: new Date('2025-12-31')
  });

  const [verificationStatus] = useState('verified');

  const [campusServices] = useState([
    {
      id: 1,
      name: 'Biblioteca Digital',
      description: 'Acceso a recursos académicos',
      type: 'library',
      available: true
    },
    {
      id: 2,
      name: 'Cafeterías Campus',
      description: 'Pagos integrados en cafeterías',
      type: 'cafeteria',
      available: true
    },
    {
      id: 3,
      name: 'Gimnasio Universitario',
      description: 'Acceso a instalaciones deportivas',
      type: 'gym',
      available: true
    },
    {
      id: 4,
      name: 'Transporte Campus',
      description: 'Autobuses internos del campus',
      type: 'transport',
      available: false
    },
    {
      id: 5,
      name: 'Estacionamiento',
      description: 'Reserva de espacios de parking',
      type: 'parking',
      available: true
    },
    {
      id: 6,
      name: 'Centro de Impresión',
      description: 'Servicios de impresión y copiado',
      type: 'printing',
      available: true
    }
  ]);

  const tabs = [
    { id: 'overview', label: 'Resumen', icon: 'Wallet' },
    { id: 'transactions', label: 'Transacciones', icon: 'Receipt' },
    { id: 'ambassador', label: 'Embajador', icon: 'Users' },
    { id: 'pickup', label: 'Puntos de Recogida', icon: 'MapPin' },
    { id: 'budget', label: 'Presupuesto', icon: 'PieChart' },
    { id: 'student-id', label: 'ID Estudiantil', icon: 'CreditCard' }
  ];

  // Add funds functionality
  const handleAddFunds = () => {
    console.log('Add funds functionality');
  };

  // Quick actions
  const quickActions = [
    {
      id: 'add-funds',
      label: 'Añadir Fondos',
      icon: 'Plus',
      variant: 'default',
      onClick: handleAddFunds
    },
    {
      id: 'view-history',
      label: 'Ver Historial',
      icon: 'History',
      variant: 'outline',
      onClick: () => setActiveTab('transactions')
    },
    {
      id: 'find-pickup',
      label: 'Puntos Cercanos',
      icon: 'MapPin',
      variant: 'outline',
      onClick: () => setActiveTab('pickup')
    }
  ];

  return (
    <div className="min-h-screen bg-background pt-16">
      <Helmet>
        <title>Billetera Universitaria - FoodRescue</title>
        <meta name="description" content="Gestiona tus créditos subsidiados, historial de transacciones y servicios del campus universitario en FoodRescue" />
      </Helmet>
      <div className="container mx-auto px-4 py-6 max-w-7xl">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8">
          <div>
            <h1 className="font-heading font-bold text-3xl text-foreground mb-2">
              Billetera Universitaria
            </h1>
            <p className="text-muted-foreground">
              Gestiona tus créditos subsidiados y servicios del campus
            </p>
          </div>
          
          {/* Quick Actions */}
          <div className="flex items-center space-x-2 mt-4 lg:mt-0">
            {quickActions?.map((action) => (
              <Button
                key={action?.id}
                variant={action?.variant}
                size="sm"
                onClick={action?.onClick}
                className="flex items-center space-x-2"
              >
                <Icon name={action?.icon} size={16} />
                <span className="hidden sm:inline">{action?.label}</span>
              </Button>
            ))}
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex space-x-1 mb-8 bg-muted rounded-lg p-1 overflow-x-auto">
          {tabs?.map((tab) => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-smooth whitespace-nowrap ${
                activeTab === tab?.id
                  ? 'bg-background text-foreground shadow-warm-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon name={tab?.icon} size={16} />
              <span>{tab?.label}</span>
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="space-y-6">
          {activeTab === 'overview' && (
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
              <div className="space-y-6">
                <WalletBalance
                  balance={walletData?.balance}
                  monthlySubsidy={walletData?.monthlySubsidy}
                  remainingCredits={walletData?.remainingCredits}
                  renewalDate={walletData?.renewalDate}
                />
                <BudgetTracker
                  budgetData={budgetData}
                  spendingInsights={spendingInsights}
                  recommendations={recommendations}
                />
              </div>
              <div className="space-y-6">
                <CampusAmbassadorProgram
                  ambassadorData={ambassadorData}
                  referrals={referrals}
                  challenges={challenges}
                />
                <PickupPointsManager
                  pickupPoints={pickupPoints?.slice(0, 2)}
                  favoritePoints={favoritePoints}
                  operatingHours={{ open: '06:00', close: '24:00' }}
                />
              </div>
            </div>
          )}

          {activeTab === 'transactions' && (
            <div className="max-w-4xl">
              <TransactionHistory transactions={transactions} />
            </div>
          )}

          {activeTab === 'ambassador' && (
            <div className="max-w-4xl">
              <CampusAmbassadorProgram
                ambassadorData={ambassadorData}
                referrals={referrals}
                challenges={challenges}
              />
            </div>
          )}

          {activeTab === 'pickup' && (
            <div className="max-w-4xl">
              <PickupPointsManager
                pickupPoints={pickupPoints}
                favoritePoints={favoritePoints}
                operatingHours={{ open: '06:00', close: '24:00' }}
              />
            </div>
          )}

          {activeTab === 'budget' && (
            <div className="max-w-4xl">
              <BudgetTracker
                budgetData={budgetData}
                spendingInsights={spendingInsights}
                recommendations={recommendations}
              />
            </div>
          )}

          {activeTab === 'student-id' && (
            <div className="max-w-4xl">
              <StudentIdIntegration
                studentData={studentData}
                verificationStatus={verificationStatus}
                campusServices={campusServices}
              />
            </div>
          )}
        </div>

        {/* Emergency Contact Info */}
        <div className="mt-12 bg-muted/30 rounded-lg p-4">
          <div className="flex items-center space-x-2 text-muted-foreground">
            <Icon name="Info" size={16} />
            <span className="text-sm">
              Para soporte técnico o problemas con tu billetera, contacta: 
              <a href="mailto:soporte@foodrescue.mx" className="text-primary hover:underline ml-1">
                soporte@foodrescue.mx
              </a>
              {' '}o llama al{' '}
              <a href="tel:+525555123456" className="text-primary hover:underline">
                +52 55 5512 3456
              </a>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UniversityWallet;